/**
 * THE CAPABILITY REEL — one minute of Leveri at work, drawn in code from the
 * product's own words (scenes.tsx), never a recording or a screenshot.
 *
 * It plays like a film: the scenes are drawn at a fixed 360 × 296 and scaled to
 * the width they are given, so a phone and a wide screen see the same frame.
 * It plays ONCE and stops on its last frame, with "Play again": the product's
 * motion law is that nothing loops, because motion must never suggest work that
 * is not happening. It plays only while it is on screen and the tab is open; it
 * can be paused, and any scene can be chosen. A person who asks their system for reduced
 * motion gets each scene's finished frame and steps through them by hand.
 *
 * The moving frame is hidden from screen readers, which get the five scenes as
 * a plain list instead: a description of the work, not a picture of it.
 */
import { useEffect, useRef, useState } from "react";
import { SCENES, STAGE } from "./scenes.js";
import { clamp01 } from "./timeline.js";

const STARTS = SCENES.reduce<number[]>((acc, s, i) => [...acc, i === 0 ? 0 : acc[i - 1]! + SCENES[i - 1]!.ms], []);
const TOTAL = SCENES.reduce((s, x) => s + x.ms, 0);
/** The finished frame of a scene: just short of its end, so the next scene has not begun. */
const finished = (i: number) => STARTS[i]! + SCENES[i]!.ms * 0.995;
const sceneAt = (t: number) => {
  let i = 0;
  while (i < SCENES.length - 1 && t >= STARTS[i + 1]!) i += 1;
  return i;
};

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function CapabilityReel({ className = "" }: { className?: string }) {
  const [reduced] = useState(prefersReducedMotion);
  const [playing, setPlaying] = useState(!reduced);
  const [t, setT] = useState(() => (reduced ? finished(0) : 0));
  const [ended, setEnded] = useState(false);
  const [onScreen, setOnScreen] = useState(true);
  const [scale, setScale] = useState(1);
  const clock = useRef(t);
  const frame = useRef<HTMLDivElement>(null);

  // The frame's width decides the scale; the scene is always drawn at the stage size.
  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    const measure = () => setScale(el.clientWidth / STAGE.width || 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Off screen, or in a hidden tab, the reel holds still: no work for a frame nobody sees.
  useEffect(() => {
    const el = frame.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => setOnScreen(entry?.isIntersecting ?? true), { threshold: 0.15 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const [tabVisible, setTabVisible] = useState(() => typeof document === "undefined" || document.visibilityState !== "hidden");
  useEffect(() => {
    const update = () => setTabVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  useEffect(() => {
    if (!playing || !onScreen || !tabVisible) return;
    let raf = 0;
    let last = performance.now();
    let painted = 0;
    const tick = (now: number) => {
      // A long pause between frames (a backgrounded phone) resumes where it was, never skips ahead.
      clock.current += Math.min(100, now - last);
      last = now;
      if (clock.current >= TOTAL) {
        clock.current = finished(SCENES.length - 1);
        setT(clock.current);
        setPlaying(false);
        setEnded(true);
        return;
      }
      if (now - painted >= 33) {
        painted = now;
        setT(clock.current);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, onScreen, tabVisible]);

  const toggle = () => {
    if (ended) {
      clock.current = 0;
      setT(0);
      setEnded(false);
      setPlaying(true);
      return;
    }
    setPlaying((v) => !v);
  };

  const jump = (i: number) => {
    setEnded(false);
    // Paused, a chosen scene shows its finished frame; playing, it plays from its start.
    const to = playing ? STARTS[i]! : finished(i);
    clock.current = to;
    setT(to);
  };

  const index = sceneAt(t);
  const scene = SCENES[index]!;
  const p = clamp01((t - STARTS[index]!) / scene.ms);
  const Draw = scene.Draw;

  return (
    <figure className="m-0">
      <div
        ref={frame}
        aria-hidden="true"
        className={`relative w-full overflow-hidden border border-rule bg-canvas ${className}`}
        style={{ height: STAGE.height * scale }}
      >
        <div className="absolute left-0 top-0 origin-top-left" style={{ width: STAGE.width, height: STAGE.height, transform: `scale(${scale})` }}>
          <Draw p={p} />
        </div>
      </div>

      <figcaption className="mt-3">
        <div className="flex items-center gap-3">
          <div className="flex flex-1 gap-1.5">
            {SCENES.map((s, i) => {
              const fill = i < index ? 1 : i > index ? 0 : p;
              return (
                <button
                  key={s.key} type="button" onClick={() => jump(i)}
                  aria-label={`${i + 1} of ${SCENES.length}: ${s.title}`} aria-current={i === index ? "step" : undefined}
                  className="flex-1 py-2"
                >
                  <span className="block h-[3px] bg-track"><span className="block h-full bg-ink" style={{ width: `${fill * 100}%` }} /></span>
                </button>
              );
            })}
          </div>
          <button
            type="button" onClick={toggle} aria-label={ended ? "Play again" : playing ? "Pause" : "Play"}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[3px] border border-rule text-ink hover:border-ink"
          >
            {ended ? (
              <svg aria-hidden="true" width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M11 6.5a4.5 4.5 0 1 1-1.3-3.2" /><path d="M10.2 0.8v2.8H7.4" /></svg>
            ) : playing ? (
              <svg aria-hidden="true" width="10" height="12" viewBox="0 0 10 12"><rect x="0" y="0" width="3" height="12" fill="currentColor" /><rect x="7" y="0" width="3" height="12" fill="currentColor" /></svg>
            ) : (
              <svg aria-hidden="true" width="10" height="12" viewBox="0 0 10 12"><path d="M0 0 L10 6 L0 12 Z" fill="currentColor" /></svg>
            )}
          </button>
        </div>
        <p className="mt-2 text-[15px] font-medium text-ink" aria-hidden="true">{scene.title}</p>
        <p className="mt-0.5 min-h-[2.9em] text-[14px] leading-snug text-stone" aria-hidden="true">{scene.line}</p>
        <ol className="sr-only">
          {SCENES.map((s) => <li key={s.key}>{s.title}. {s.line}</li>)}
        </ol>
        <p className="mt-1 text-[12px] text-stone">Sample clients and figures.</p>
      </figcaption>
    </figure>
  );
}
